import {useEffect, useRef, useState} from 'react';

// Maps the /me penalty state (polled) to the presentational PenaltyShootout props, and drives the
// OPEN → REVEAL → next choreography. On arrival mid-shootout (refresh / late entry) the already-resolved
// kicks are NOT replayed — we jump past the history so the player lands on the current step.
export function usePenaltyShootoutView({penaltyState, penaltyBoard, homeTeam, awayTeam, onPredict}) {
    const kicks = penaltyState?.kicks ?? [];
    const openKick = penaltyState?.openKick ?? null;

    // revealedCount = kicks whose reveal has completed (committed to the visible history/score).
    const [revealedCount, setRevealedCount] = useState(0);
    const [revealing, setRevealing] = useState(null); // {index, taker, outcome} | null
    const [ready, setReady] = useState(false);
    const initialized = useRef(false);

    // On entering the shootout: skip past the kicks already resolved (no replay). `ready` only flips
    // true once revealedCount has been seeded, so the reveal-start effect can't fire on the stale 0.
    useEffect(() => {
        if (!penaltyState) {
            initialized.current = false;
            setReady(false);
            setRevealing(null);
            setRevealedCount(0);
            return;
        }
        if (!initialized.current) {
            initialized.current = true;
            setRevealedCount(penaltyState.kicks.length);
            setReady(true);
        }
    }, [penaltyState]);

    // Start the next reveal (one at a time). Guarded by `ready` (anti-race) and `revealing`.
    useEffect(() => {
        if (!ready || revealing) return;
        if (revealedCount >= kicks.length) return;
        setRevealing(kicks[revealedCount]);
    }, [ready, revealing, revealedCount, kicks.length]);

    // Arm the reveal timer in its OWN effect keyed on `revealing`, so the timeout survives the
    // setRevealing re-render (a single combined effect would clear it on cleanup → frozen reveal).
    useEffect(() => {
        if (!revealing) return;
        const timer = setTimeout(() => {
            setRevealedCount((c) => c + 1);
            setRevealing(null);
        }, 2500);
        return () => clearTimeout(timer);
    }, [revealing]);

    if (!penaltyState) return null;

    // History/score reflect only the COMMITTED kicks (revealed) — the kick being revealed is not counted
    // yet, so the score updates in sync with the reveal instead of spoiling it.
    const committed = kicks.slice(0, revealedCount);
    const score = committed.reduce(
        (acc, k) => {
            if (k.outcome === 'GOAL') k.taker === 'HOME' ? acc.home++ : acc.away++;
            return acc;
        },
        {home: 0, away: 0}
    );
    const history = committed.map((k) => ({taker: k.taker, result: k.outcome}));

    // The dedicated /penalty-leaderboard already returns only shootout players, ranked, with their kicks.
    const board = (penaltyBoard ?? []).map((r) => ({
        id: r.pseudo, isMe: r.isMe, pseudo: r.pseudo, rank: r.rank,
        points: Math.round(r.score ?? 0), penaltyKicks: r.kicks ?? [],
    }));

    return {
        homeTeam: homeTeam ?? {shortName: ''},
        awayTeam: awayTeam ?? {shortName: ''},
        phase: revealing ? 'REVEAL' : 'OPEN',
        kickNumber: revealing ? revealing.index : (openKick?.index ?? kicks.length + 1),
        taker: revealing ? revealing.taker : (openKick?.taker ?? 'HOME'),
        actualResult: revealing?.outcome ?? null,
        myPrediction: revealing ? null : (penaltyState.myPrediction ?? null),
        onPredict: (outcome) => {
            if (openKick) onPredict(openKick.id, outcome);
        },
        lastOutcome: null, // per-kick personal feedback not exposed yet (kept simple)
        score,
        history,
        board,
        myStreak: 0,
    };
}
